/* eslint-disable react/prop-types */
import React, { useEffect, Fragment, createRef, useContext } from "react";
import { connect, useSelector } from "react-redux";

import API from "constants/api";
import {
  resetState,
  getObject,
  setObjectParameters,
} from "actions/objectActions";
import { createObjectWithGeometry } from "actions/spectrumActions";
import { validateObject } from "validation/objectValidation";
import LocaleContext from "constants/localization";
import {
  getResourceDisplayValue,
  getResourceFormatChain,
} from "actions/resourceActions";
import { SPECTRUM } from "constants/predicates";
import { setAlert } from "actions/viewActions";
import { getUniquenessMessage } from "actions/validationActions";
import { addValue } from "actions/fieldActions";
import { isSaveDisabled } from "helpers/objectHelpers";

import Loading from "components/common/Loading";
import SaveButton from "components/common/SaveButton";
import ArchivedObject from "components/archived-object/ArchivedObject";

import FieldEdit from "field/edit/FieldEdit";
import TemplateOptions from "./TemplateOptions";
import DocumentCountSelector from "./DocumentCountSelector";
import { useState } from "react";

const CreateView = (props) => {
  const LS = useContext(LocaleContext);
  const [isSaving, setIsSaving] = useState(false);
  const { view, parentUri, uri, tabs, loading, validated } = props;
  const formRef = createRef();
  const allowedDocumentCountViews = [
    "AcquisitionInformation",
    "CollectionObject",
    "ImageArchive",
    "Art",
    "Archive",
    "Building",
    "Property",
    "BuiltEnvironmentSite",
    "ArcheologicalSite",
    "Area",
    "OtherSite",
  ];
  const showDocumentCountSelector = allowedDocumentCountViews.includes(view);
  const objectUri = useSelector((state) => state.object.uri);
  const resources = useSelector((state) => state.modal.resources);
  const fields = useSelector((state) => state.field.fields);

  useEffect(() => {
    props.setObjectParameters({ view, uri, parentUri });
    props
      .getObject(`${API.UI.CREATE_OBJECT}&view=${view}&parentUri=${parentUri}`)
      .then(() => {
        window?.parent?.reloadTree(parentUri);
        // Creating new object from Acquisition, set value automatically
        if (props.acquisition) {
          const predicate = SPECTRUM.OBJECT_ACQUISITION_INFORMATION;
          const resourceFormatChain = getResourceFormatChain(
            resources,
            predicate
          );
          getResourceDisplayValue(
            props.acquisition,
            predicate,
            "",
            objectUri,
            view,
            resourceFormatChain
          ).then((value) => props.addValue(predicate, value));
        }
        // Creating new object from BuiltEnvironmentSite, set value automatically
        if (props.builtSite) {
          const predicate = SPECTRUM.CE.LINK_TO_BUILT_SITE;
          const resourceFormatChain = getResourceFormatChain(
            resources,
            predicate
          );
          getResourceDisplayValue(
            props.builtSite,
            predicate,
            "",
            objectUri,
            view,
            resourceFormatChain
          ).then((value) => props.addValue(predicate, value));
        }
      });
    return () => props.resetState();
  }, []);

  const validateOnSave = () => {
    setIsSaving(true);
    const { isValid, invalidFields } = props.validateObject(formRef);

    if (isValid) {
      const uniqMessage = getUniquenessMessage({ view: view }, LS);
      return props.createObjectWithGeometry(uniqMessage) && setIsSaving(false);
    } else {
      setAlert({
        header: LS.error.invalidFields,
        contents: invalidFields.join("\n"),
        variant: "danger",
      });
    }
    setIsSaving(false);
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Fragment>
      <SaveButton
        disabled={isSaving || isSaveDisabled(fields)}
        onSubmit={() => validateOnSave(view)}
      />
      <br />
      {showDocumentCountSelector && <DocumentCountSelector />}
      <br />
      <TemplateOptions />
      <ArchivedObject
        tabs={tabs}
        Field={FieldEdit}
        formRef={formRef}
        validated={validated}
      />
      <SaveButton
        disabled={isSaving || isSaveDisabled(fields)}
        onSubmit={() => validateOnSave(view)}
      />
    </Fragment>
  );
};

const mapStateToProps = (state) => {
  return {
    validated: state.object.validated,
    loading: state.object.loading,
    tabs: state.object.tabs,
  };
};

export default connect(mapStateToProps, {
  getObject,
  setObjectParameters,
  resetState,
  createObjectWithGeometry,
  setAlert,
  validateObject,
  addValue,
})(CreateView);
